import {
  updateGroupedCashGames,
  updateGroupedOmahaCashGames,
  updateOmahaCashGames,
  updateTexasCashGames
} from 'reducers/lobby';
import { groupBy, sortData } from 'utils/functions';

export interface CashGamesGroup {
  gameName: string;
  stakes: string;
  maxPlayers: number;
  tablesCount: number;
  tables: any;
}

export interface LobbySortOpts {
  hasFilter: boolean;
  sortingField: any;
  sortingDirection: any;
}

/**
 * Group the `{ texas, omaha }` cash-game snapshot by seats→stakes and dispatch
 * it into the Redux lobby store. This is the portion of the lobby ingestion
 * that is identical for logged-in players and guests, shared by both
 * `Lobby` (member) and `GuestLobby` so the two never drift. Returns the raw
 * (optionally sorted) per-variant arrays so the member path can run its
 * player-specific derivation (seated-table detection / auto-subscribe) on the
 * exact same data.
 */
export function dispatchLobbyCashGames(
  receivedCashGames: any,
  dispatch: (action: any) => void,
  { hasFilter, sortingField, sortingDirection }: LobbySortOpts
): { rawTexasGames: any[]; rawOmahaGames: any[] } {
  const texasGames = receivedCashGames['texas'];
  const groupedByseats = groupBy(texasGames, 'seats');
  Object.keys(groupedByseats).forEach((seat) => {
    groupedByseats[seat] = groupBy(groupedByseats[seat], 'stakes');
  });

  let groupedFinal: CashGamesGroup[] = [];
  let rawTexasGames = receivedCashGames.texas;
  Object.keys(groupedByseats).forEach((seat) => {
    Object.keys(groupedByseats[seat]).forEach((stake) => {
      groupedFinal.push({
        gameName: 'texas',
        stakes: `${stake}`,
        maxPlayers: Number(seat),
        tablesCount: groupedByseats[seat][stake].length,
        tables: groupedByseats[seat][stake]
      });
    });
  });

  if (hasFilter && sortingField !== null && sortingDirection !== null) {
    groupedFinal = sortData(groupedFinal, sortingField, sortingDirection);
    rawTexasGames = sortData(rawTexasGames, sortingField, sortingDirection);
  }

  const omahaGames = receivedCashGames['omaha'];
  const groupedOmahaBySeats = groupBy(omahaGames, 'seats');
  Object.keys(groupedOmahaBySeats).forEach((seat) => {
    groupedOmahaBySeats[seat] = groupBy(groupedOmahaBySeats[seat], 'stakes');
  });

  let groupedOmahaFinal: CashGamesGroup[] = [];
  let rawOmahaGames = receivedCashGames.omaha;
  Object.keys(groupedOmahaBySeats).forEach((seat) => {
    Object.keys(groupedOmahaBySeats[seat]).forEach((stake) => {
      groupedOmahaFinal.push({
        gameName: 'omaha',
        stakes: `${stake}`,
        maxPlayers: Number(seat),
        tablesCount: groupedOmahaBySeats[seat][stake].length,
        tables: groupedOmahaBySeats[seat][stake]
      });
    });
  });

  if (hasFilter && sortingField !== null && sortingDirection !== null) {
    // Sort the GROUPED structure (mirrors the texas branch above) — the grouped
    // Omaha store must hold CashGamesGroup[], not the flat table list.
    groupedOmahaFinal = sortData(groupedOmahaFinal, sortingField, sortingDirection);
    rawOmahaGames = sortData(rawOmahaGames, sortingField, sortingDirection);
  }

  dispatch(updateGroupedCashGames(groupedFinal));
  dispatch(updateTexasCashGames(rawTexasGames));
  dispatch(updateOmahaCashGames(rawOmahaGames));
  dispatch(updateGroupedOmahaCashGames(groupedOmahaFinal));

  return { rawTexasGames, rawOmahaGames };
}
